import type { Ref } from 'vue'
import { streamSseEvents } from '../lib/streamSse'
import type { Citation, ThreadMessage, ThreadState } from '../stores/threads'

function deriveTitle(question: string): string {
  const first = question.split(/\n/)[0]?.trim() ?? ''
  if (!first) return 'Untitled thread'
  const max = 80
  return first.length > max ? `${first.slice(0, max - 1).trimEnd()}…` : first
}

function buildNewThread(args: {
  id: number
  question: string
  userTurn: ThreadMessage
  assistantTurn: ThreadMessage
}): ThreadState {
  return {
    id: args.id,
    title: deriveTitle(args.question),
    messages: [args.userTurn, args.assistantTurn],
    last_message_at: args.assistantTurn.created_at!,
    created_at: args.userTurn.created_at!,
  }
}

/**
 * Owns the two `submit` transports (`trySubmitStreaming` + `submitSync`) and
 * the shared error-rollback logic, operating directly on the threads store's
 * `current` / `submitError` refs. Split out of `stores/threads.ts` per that
 * file's own EXTRACTION SEAM comment once it crossed the 300-line ceiling —
 * `open` / `loadRecent` / `reset` / `cancel` stay store-resident.
 */
export function useThreadSubmitter(current: Ref<ThreadState | null>, submitError: Ref<string | null>) {
  function appendAssistantTurn(question: string, userTurn: ThreadMessage, assistantTurn: ThreadMessage): void {
    if (current.value) {
      current.value.messages = [...current.value.messages, assistantTurn]
      return
    }
    // First turn in a new thread (or unauthenticated fallback, conversation_id
    // null on the server — no row persisted, but we still project the answer
    // locally so the UI stays responsive).
    current.value = buildNewThread({ id: 0, question, userTurn, assistantTurn })
  }

  /**
   * Returns true if at least one delta was received (the stream actually
   * started), false if the connection failed BEFORE any data arrived
   * (caller falls back to the sync path). Throws on mid-stream failure
   * and on AbortError — those propagate to the caller's catch.
   */
  async function trySubmitStreaming(question: string, userTurn: ThreadMessage, signal: AbortSignal): Promise<boolean> {
    let res: Response
    try {
      res = await fetch('/api/v1/queries/stream', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', Accept: 'text/event-stream' },
        body: JSON.stringify({ question, conversation_id: current.value?.id }),
        signal,
      })
    } catch (e) {
      // Network-layer failure before headers landed (DNS, TCP, TLS handshake).
      // AbortError still surfaces here — bubble it; the caller distinguishes.
      if (e instanceof DOMException && e.name === 'AbortError') throw e
      return false
    }
    if (!res.ok || !res.body) return false

    // Append the assistant turn LAZILY on the first event (delta or end) so
    // a stream that opens but closes without writing anything (server crash
    // between the `: stream-open` comment flush and the first real write,
    // network partition mid-handler) doesn't leave an empty bubble behind
    // for the sync fallback to duplicate. The cost is a brief window where
    // the user sees "Thinking…" without a bubble even after the stream's
    // connected — but in the typical case the first delta arrives within
    // the same tick that opens the stream, so the gap is invisible.
    let assistantIdx = -1
    const ensureAssistantTurn = () => {
      if (assistantIdx !== -1) return
      appendAssistantTurn(question, userTurn, {
        role: 'assistant',
        content: '',
        citations: [],
        created_at: new Date().toISOString(),
      })
      assistantIdx = current.value!.messages.length - 1
    }

    let sawDelta = false
    let endPayload: { citations?: Citation[]; conversation_id?: number | null; weak_citation?: boolean } | undefined

    for await (const event of streamSseEvents(res)) {
      if (event.event === 'message') {
        // Default-event SSE frame from the backend's `write_sse(delta)`.
        // Vue 3 deep reactivity on the messages array picks up the nested
        // mutation without a full slice.
        if (typeof event.data === 'string') {
          ensureAssistantTurn()
          current.value!.messages[assistantIdx].content += event.data
          sawDelta = true
        }
      } else if (event.event === 'end') {
        ensureAssistantTurn()
        endPayload = event.data as typeof endPayload
      } else if (event.event === 'error') {
        const msg = (event.data as { error?: string } | undefined)?.error || 'stream error'
        throw new Error(msg)
      }
    }

    if (endPayload && assistantIdx !== -1) {
      const messages = current.value!.messages
      messages[assistantIdx].citations = endPayload.citations ?? []
      messages[assistantIdx].weak_citation = endPayload.weak_citation ?? false
      if (current.value && endPayload.conversation_id && endPayload.conversation_id !== current.value.id) {
        current.value.id = endPayload.conversation_id
      }
      current.value!.last_message_at = messages[assistantIdx].created_at!
    }

    return sawDelta || !!endPayload
  }

  async function submitSync(question: string, userTurn: ThreadMessage, signal: AbortSignal): Promise<void> {
    const res = await fetch('/api/v1/queries', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ question, conversation_id: current.value?.id }),
      signal,
    })
    if (!res.ok) {
      const body = (await res.json().catch(() => ({}))) as { error?: string }
      throw new Error(body.error || `query failed (${res.status})`)
    }
    const data = (await res.json()) as {
      answer: string
      citations: Citation[]
      conversation_id: number | null
      weak_citation?: boolean
    }
    const assistantTurn: ThreadMessage = {
      role: 'assistant',
      content: data.answer,
      citations: data.citations ?? [],
      weak_citation: data.weak_citation ?? false,
      created_at: new Date().toISOString(),
    }
    appendAssistantTurn(question, userTurn, assistantTurn)
    if (current.value && data.conversation_id && current.value.id !== data.conversation_id) {
      current.value.id = data.conversation_id
    }
    if (current.value) current.value.last_message_at = assistantTurn.created_at!
  }

  function rollbackOnError(userTurn: ThreadMessage, e: unknown): void {
    const messages = current.value?.messages
    if (messages?.length) {
      // Pop the empty / partial assistant bubble first (streaming path),
      // then the optimistic user turn — but ONLY if the user turn is still
      // the immediate predecessor (i.e. nothing committed between them).
      if (messages.at(-1)?.role === 'assistant' && messages.at(-1)?.content === '') {
        current.value!.messages = messages.slice(0, -1)
      }
      const lastUser = current.value!.messages.at(-1)
      if (lastUser?.role === 'user' && lastUser.content === userTurn.content) {
        current.value!.messages = current.value!.messages.slice(0, -1)
      }
    }
    if (e instanceof DOMException && e.name === 'AbortError') {
      submitError.value = null
    } else {
      submitError.value = e instanceof Error ? e.message : 'query failed'
    }
  }

  return { trySubmitStreaming, submitSync, rollbackOnError }
}
